import React, { useContext, useEffect, useState, useRef, Fragment } from "react";
import { getMeterInventory, updateInventory } from "../../service/Inventory";
import { toast } from "../../utils/toast";
import { Spinner } from "../../Spinner";
import { Dialog, Transition } from "@headlessui/react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import ModalCloseButton from "../common/ModalCloseButton";
import useDirection from "../../hooks/useDirection";

export const UpdateStock = ({ open, barcode, onClose, onSaved }) => {
  const [meterDetails, setMeterDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { theme, selectedOrgId, langMode } = useContext(AppContext);
  const { isRTL: isRtl, dir } = useDirection();
  const cancelButtonRef = useRef(null);

  useEffect(() => {
    if (!open || !barcode) return;
    const fetchMeterInventory = async () => {
      try {
        setLoading(true);
        setError(null);
        setMeterDetails(null);
        const data = await getMeterInventory(barcode, selectedOrgId);
        const { transferredFrom, transferredTo, ...filteredFields } =
          data.body.value;
        setMeterDetails(filteredFields);
      } catch (err) {
        setError(translations[langMode].failedtofetchmeter);
        toast.error(translations[langMode].failedtofetchmeter);
      } finally {
        setLoading(false);
      }
    };

    fetchMeterInventory();
  }, [open, barcode, selectedOrgId, langMode]);

  const handleInputChange = (key, value) => {
    setMeterDetails((prevDetails) => ({
      ...prevDetails,
      [key]: value,
    }));
  };

  const UpdateInventoryDetails = async () => {
    try {
      setIsLoading(true);
      const success = await updateInventory(meterDetails);
      setIsLoading(false);
      if (success) {
        toast.success(translations[langMode].userStatusupdatesuc);
        onSaved?.();
      } else {
        toast.error(translations[langMode].failedtoupdateuserstatus);
      }
    } catch (error) {
      setIsLoading(false);
      toast.error(
        error?.response?.data?.body?.value ||
          error?.body?.value ||
          translations[langMode].failedtoupdateuserstatus
      );
    }
  };

  const getFieldLabel = (key) => {
    const keyMap = {
      meterBarCode: translations[langMode]?.meterBarCode,
      projectId: translations[langMode]?.project,
      components: translations[langMode]?.components,
      meterType: translations[langMode]?.meterType,
      manufacturingYear: translations[langMode]?.manufacturingYear,
      meterMake: translations[langMode]?.meterMake,
      activationDate: translations[langMode]?.activationDate,
      batchNo: translations[langMode]?.batchNo,
      installationStatus: translations[langMode]?.installationStatus,
      createdBy: translations[langMode]?.createBy,
      createdAt: translations[langMode]?.createdAt,
      simNo: translations[langMode]?.simNo,
      ipAddress: translations[langMode]?.ipAddress,
      iccid: translations[langMode]?.iccid,
      apn: translations[langMode]?.apn,
      op: translations[langMode]?.op || "Operator",
      balance: translations[langMode]?.balance,
      status: translations[langMode]?.status,
      simDetailUpdatedAt: translations[langMode]?.simDetailUpdatedAt,
      transferredFrom: translations[langMode]?.transferredFrom,
      transferredTo: translations[langMode]?.transferredTo,
    };
    return (
      keyMap[key] ||
      translations[langMode]?.[key] ||
      (key === "op"
        ? "Operator"
        : key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase()))
    );
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[100]"
        initialFocus={cancelButtonRef}
        onClose={onClose}
        dir={dir}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-[100] w-screen overflow-y-auto">
          <div className="flex min-h-full justify-center p-2.5 sm:p-4 text-center items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={`relative transform overflow-hidden rounded-xl sm:rounded-2xl bg-white shadow-xl transition-all sm:my-8 w-full max-w-4xl max-h-[88vh] flex flex-col ${
                  theme === "dark"
                    ? "dark-mode"
                    : theme === "high-contrast"
                    ? "high-contrast"
                    : ""
                }`}
              >
                {loading && <Spinner />}
                <div className="shrink-0 flex justify-between items-center px-4 sm:px-6 py-3.5 sm:py-4 border-b border-slate-100 dark:border-slate-700">
                  <h4
                    className={`text-sm sm:text-base font-semibold ${
                      theme === "dark" ? "text-white" : "text-gray-800"
                    } ${
                      theme === "high-contrast" ? "text-black" : "text-gray-700"
                    }`}
                  >
                    {translations[langMode].updatemeterDEtail}
                  </h4>
                  <ModalCloseButton onClick={onClose} />
                </div>
                <div
                  className={`flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 bg-white dark:bg-slate-900 flex flex-col ${
                    theme === "dark"
                      ? "dark-mode"
                      : theme === "high-contrast"
                      ? "high-contrast"
                      : ""
                  }`}
                >
                  {loading ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center text-gray-500">
                      <p className="text-sm font-medium">
                        {translations[langMode].loadingmeterdetail}
                      </p>
                    </div>
                  ) : error ? (
                    <div className="flex items-center justify-center py-12 text-center text-red-500 text-sm font-medium">
                      <p>{error}</p>
                    </div>
                  ) : meterDetails ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                      {Object.entries(meterDetails).map(([key, value]) => {
                        if (key === "organizationId" || key === "id") return null;
                        const fieldLabel = getFieldLabel(key);
                        const isReadOnly = key === "meterBarCode";

                        return (
                          <div key={key} className="flex flex-col">
                            <label
                              htmlFor={`meter-input-${key}`}
                              className={`text-xs font-semibold mb-1.5 ${
                                isRtl ? "text-right" : "text-left"
                              } ${
                                theme === "dark"
                                  ? "text-slate-200"
                                  : theme === "high-contrast"
                                  ? "text-black"
                                  : "text-slate-700"
                              }`}
                            >
                              {fieldLabel}
                              {isReadOnly && (
                                <span className="ms-1.5 text-[11px] font-normal text-slate-400">
                                  ({translations[langMode]?.readOnly || "Read-only"})
                                </span>
                              )}
                            </label>
                            {key === "createdAt" || key === "activationDate" ? (
                              <input
                                id={`meter-input-${key}`}
                                type="datetime-local"
                                value={
                                  value && !isNaN(Date.parse(value))
                                    ? new Date(
                                        new Date(value).getTime() -
                                          new Date().getTimezoneOffset() * 60000
                                      )
                                        .toISOString()
                                        .slice(0, 16)
                                    : ""
                                }
                                onChange={(e) => {
                                  const localDate = new Date(e.target.value);
                                  const utcDate = new Date(
                                    localDate.getTime() +
                                      localDate.getTimezoneOffset() * 60000
                                  );
                                  handleInputChange(key, utcDate.toISOString());
                                }}
                                className={`w-full h-10 px-3 text-sm rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] ${
                                  theme === "dark"
                                    ? "bg-slate-800 border-slate-700 text-white"
                                    : theme === "high-contrast"
                                    ? "bg-white border-black text-black"
                                    : "bg-white border-slate-200 text-slate-800"
                                }`}
                              />
                            ) : (
                              <input
                                id={`meter-input-${key}`}
                                type="text"
                                value={
                                  value !== null && value !== undefined
                                    ? value
                                    : ""
                                }
                                onChange={(e) =>
                                  handleInputChange(key, e.target.value)
                                }
                                readOnly={isReadOnly}
                                placeholder={`${translations[langMode]?.enter || "Enter"} ${fieldLabel}`}
                                className={`w-full h-10 px-3 text-sm rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] ${
                                  isReadOnly
                                    ? "bg-slate-100 text-slate-500 border-slate-200 cursor-not-allowed select-none dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"
                                    : theme === "dark"
                                    ? "bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                                    : theme === "high-contrast"
                                    ? "bg-white border-black text-black"
                                    : "bg-white border-slate-200 text-slate-800 placeholder-slate-400"
                                }`}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      {translations[langMode].nometerdetailfound} {barcode}
                    </p>
                  )}
                </div>
                <div
                  className={`shrink-0 sticky bottom-0 bg-white dark:bg-slate-900 flex justify-end items-center gap-3 border-t border-slate-100 dark:border-slate-700 px-4 sm:px-6 py-3.5 sm:py-4 ${
                    theme === "dark"
                      ? "dark-mode"
                      : theme === "high-contrast"
                      ? "high-contrast"
                      : ""
                  }`}
                >
                  <CommonButton
                    colorIntent="danger"
                    variant="danger"
                    onClick={onClose}
                    theme={theme}
                  >
                    {translations[langMode].cancel}
                  </CommonButton>
                  <CommonButton
                    colorIntent="success"
                    variant="primary"
                    onClick={UpdateInventoryDetails}
                    disabled={isLoading || loading}
                    theme={theme}
                  >
                    {isLoading
                      ? translations[langMode].Saving
                      : translations[langMode].save}
                  </CommonButton>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
